/**
 * Party color utilities for consistent party affiliation styling across the application.
 *
 * Uses semantic party color tokens defined in tailwind.config.js and the canonical
 * registry in ../constants/parties.ts.
 */

import {
  formatPartyDisplay as formatPartyDisplayFromRegistry,
  matchesPartyFilter as matchesPartyFilterFromRegistry,
  partyFilterMatchValues,
  resolveParty,
} from '../constants/parties';

export {
  formatPartyDisplayFromRegistry as formatPartyDisplay,
  matchesPartyFilterFromRegistry as matchesPartyFilter,
  partyFilterMatchValues,
  resolveParty,
};

/**
 * Get text color classes for party affiliation (for names, links, etc.)
 */
export const getPartyTextColor = (party?: string): string => {
  if (!party) return 'text-party-unknown dark:text-party-unknown-light';

  const entry = resolveParty(party);
  if (entry?.id === 'democratic') {
    return 'text-party-democrat dark:text-party-democrat-light';
  }
  if (entry?.id === 'republican') {
    return 'text-party-republican dark:text-party-republican-light';
  }
  if (entry?.id === 'independent' || entry?.id === 'unaffiliated') {
    return 'text-party-independent dark:text-party-independent-light';
  }

  const p = party.toUpperCase();
  if (p.includes('INDEPENDENT')) {
    return 'text-party-independent dark:text-party-independent-light';
  }
  if (p.includes('DEMOCRAT') || p === 'D') {
    return 'text-party-democrat dark:text-party-democrat-light';
  }
  if (p.includes('REPUBLICAN') || p === 'R') {
    return 'text-party-republican dark:text-party-republican-light';
  }
  return 'text-party-independent dark:text-party-independent-light';
};

/**
 * Get badge/chip color classes for party affiliation (background + text)
 */
export const getPartyBadgeColor = (party?: string): string => {
  if (!party) return 'bg-party-unknown-bg text-party-unknown-text dark:bg-party-unknown-bg-dark dark:text-party-unknown-text-dark';

  const entry = resolveParty(party);
  if (entry?.id === 'democratic') {
    return 'bg-party-democrat-bg text-party-democrat-text dark:bg-party-democrat-bg-dark dark:text-party-democrat-light';
  }
  if (entry?.id === 'republican') {
    return 'bg-party-republican-bg text-party-republican-text dark:bg-party-republican-bg-dark dark:text-party-republican-light';
  }
  return 'bg-party-independent-bg text-party-independent-text dark:bg-party-independent-bg-dark dark:text-party-independent-light';
};

/**
 * Get Chip component variant for party affiliation
 */
export const getPartyChipVariant = (party?: string): 'info' | 'danger' | 'secondary' => {
  if (!party) return 'secondary';

  const entry = resolveParty(party);
  if (entry?.id === 'democratic') return 'info';
  if (entry?.id === 'republican') return 'danger';
  return 'secondary';
};

/**
 * Get party abbreviation (D, R, I, etc.)
 */
export const getPartyAbbreviation = (party?: string): string => {
  const trimmed = String(party ?? '').trim();
  if (!trimmed) return '';

  const entry = resolveParty(trimmed);
  if (entry?.abbrev) return entry.abbrev;

  const p = trimmed.toUpperCase();
  if (p.includes('INDEPENDENT') || p === 'I') return 'I';
  if (p.includes('DEMOCRAT') || p === 'D') return 'D';
  if (p.includes('REPUBLICAN') || p === 'R') return 'R';
  return trimmed.charAt(0).toUpperCase();
};
